import React, { useContext } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  FlightGridRow,
  FlightShopRow,
  formatInterval,
  getTotalPriceText,
  B2BFlightCardV2,
  TriangleIcon,
  useDeviceTypes,
  IconName,
  Icon,
  ButtonWrap,
  removeTimezone,
  MobilePopoverCard,
  Header,
  BackButton,
  LoadingIndicator,
  B2BSpinner,
  FareDetailsCardCtaTitles,
  FareDetailsCardCtaType,
  usePrevious,
  PackageSavingPill,
  isSpendAndGetOffer,
  SanitizedHtml,
} from "halifax";
import clsx from "clsx";
import * as textConstants from "./textConstants";
import {
  FlightRatingsEnum,
  Flights,
  IFlightGridFares,
  Slice,
  TravelWalletOffer,
  TravelWalletCredit,
  RewardsAccount,
  PolicyViolation,
  TripCategory,
  IFlightListData,
  FareclassOptionFilter,
  SafePackageDetails,
  DateRange,
  TripDetails,
  FiatPrice,
  Airport,
} from "redmond";
import "./styles.scss";
import {
  getConfigTenant,
  isCorpTenant,
  SHOW_POLICIES_IN_PACKAGES,
  useIsTravelApp,
  useShowPolicyBanner,
} from "@capone/common";
import dayjs from "dayjs";
import { ClientContext } from "../../../../../../App";
import { FlightDetails } from "../FlightDetails";
import { IOpenModal, ModalTypes } from "../../component";
import {
  AVAILABLE,
  PACKAGES_TAG_EXCLUSIVE_RATES,
  getExperimentVariant,
  useExperiments,
} from "../../../../../../context/experiments";

export enum FlightCardType {
  content = "content",
  skeleton = "skeleton",
}

interface IFlightListInfoBaseProps {
  className?: string;
  type: FlightCardType;
}

export const isFlightMultiTicketType = ({
  flight,
  slices,
}: {
  flight?: IFlightListData;
  slices?: Record<string, Slice>;
}) => {
  if (flight && slices) {
    const { hasSelfTransferLayover } = slices[flight.slice];
    return hasSelfTransferLayover;
  }
  return false;
};

export interface IFlightListInfoContentProps extends IFlightListInfoBaseProps {
  selectedFare: any;
  slice: Slice;
  onClick?: (selectedFareClass: string) => void;
  onFareClick?: (fareId: string) => void;
  isExpanded?: boolean;
  type: FlightCardType.content;
  flights: Flights;
  flight: IFlightListData;
  rewardsKey: string;
  fareClassFilter: FareclassOptionFilter;
  onAlgomerchClick: (val: string) => void;
  maxFlightPriceFilter: number;
  isRoundTrip: boolean;
  offersByTripId?: { [key: string]: TravelWalletOffer };
  showPriceAndTags?: boolean;
  credit?: TravelWalletCredit;
  onClickSelfCheckBanner?: () => void;
  isChatbotEnabled?: boolean;
  onOpenPolicyDescriptor?: (
    entryPoint: string,
    policyReasons: PolicyViolation[],
  ) => void;
  isOutgoing: boolean;
  isInPolicyFilter?: boolean;
  isAgentPortal?: boolean;
  packageNightCountBasedOnAvailability: number | null;
  packageNightCountBasedOnOutboundFlight: number | null;
  packagesByFareSlice: { [key: string]: SafePackageDetails };
  isRecommendedFlight?: boolean;
  departureDate: Date | null;
  returnDate: Date | null;
  hotelShopDateRange: DateRange | null;
  selectedRewardsAccount?: RewardsAccount;
  canEarnRewards?: boolean;
}

export interface IFlightListInfoSkeletonProps extends IFlightListInfoBaseProps {
  type: FlightCardType.skeleton;
}

interface GridFaresPerShelf {
  basic: number;
  standard: number;
  premium: number;
  enhanced: number;
  luxury: number;
}

const FARE_GRID_TAG_LIMIT = 2;

export const FlightListInfoContent = (props: IFlightListInfoContentProps) => {
  const {
    selectedFare,
    slice,
    isExpanded,
    flight,
    onClick,
    flights,
    rewardsKey,
    fareClassFilter,
    onAlgomerchClick,
    onFareClick,
    maxFlightPriceFilter,
    isRoundTrip,
    offersByTripId,
    showPriceAndTags = true,
    credit,
    isChatbotEnabled,
    isAgentPortal,
    packageNightCountBasedOnAvailability,
    packageNightCountBasedOnOutboundFlight,
    packagesByFareSlice,
    isRecommendedFlight,
    isOutgoing,
    selectedRewardsAccount,
    canEarnRewards,
  } = props;

  const expState = useExperiments();

  const getFareSliceId = (fare: any) => fare?.fareSlice || fare.return;

  const getFareId = (fare: any) => fare.example?.fare || fare.id;

  const { matchesMobile, matchesDesktop, matchesLargeDesktop } =
    useDeviceTypes();

  const showEarnEnhancement =
    !!selectedRewardsAccount &&
    !!selectedRewardsAccount.earn.flightsMultiplier &&
    canEarnRewards !== false;

  const matchesMediumDesktopOnly = matchesDesktop && !matchesLargeDesktop;

  const airline = flights.airlines[slice.marketingAirline];
  const airlineCode = airline ? airline.code : slice.marketingAirline;
  const airlineName = airline ? airline.displayName : slice.marketingAirline;

  function getFlightGridFares(flight: any, _?: string) {
    const fares: IFlightGridFares = {
      basic: null,
      standard: null,
      premium: null,
      enhanced: null,
      luxury: null,
    };

    const faresPerShelf = flight.fares.reduce(
      (result: GridFaresPerShelf, fare: any) => {
        const fareSlice = flights.fareSlices[getFareSliceId(fare)];
        result[FlightRatingsEnum[fareSlice.fareShelf.value]] += 1;
        return result;
      },
      {
        basic: 0,
        standard: 0,
        premium: 0,
        enhanced: 0,
        luxury: 0,
      },
    );

    flight.fares.forEach((fare: any) => {
      const fareSliceId = getFareSliceId(fare);
      const fareSlice = flights.fareSlices[fareSliceId];
      const packageByFareSlice = packagesByFareSlice[fareSliceId];

      if (packageByFareSlice) {
        const gridFare = {
          fareId: getFareId(fare),
          tripId: fare.tripId,
          fareName: fareSlice.fareBrandName ?? "",
          // note: rawPrice is used in the price-range filtering logic, so it shouldn't include the CFAR price
          rawPrice: packageByFareSlice.pricing.pricePerTraveler.fiat.value,
          tags: [],
          price: "",
          prices: {},
          additionalFares: 0,
        };

        gridFare.price = getTotalPriceText({
          price: packageByFareSlice.incrementalPricePerTraveler?.fiat,
        });
        gridFare.prices = packageByFareSlice.pricing;

        // //  When there are multiple fares per shelf, set additional fares count to display
        if (faresPerShelf[FlightRatingsEnum[fareSlice.fareShelf.value]] > 1) {
          gridFare.additionalFares =
            faresPerShelf[FlightRatingsEnum[fareSlice.fareShelf.value]] - 1;
        }

        // When there are multiple fares per shelf, display the lowest on the grid level
        fares[FlightRatingsEnum[fareSlice.fareShelf.value]] = getLowerGridFare(
          fares[FlightRatingsEnum[fareSlice.fareShelf.value]],
          gridFare,
        );
      }
    });
    return fares;
  }

  function getLowerGridFare(existingFare: any, newFare: any) {
    const existingPrice = existingFare?.rawPrice;
    return existingPrice && existingPrice < newFare.rawPrice
      ? existingFare
      : newFare;
  }

  const gridFares = React.useMemo(
    () => getFlightGridFares(flight, rewardsKey),
    [flight, rewardsKey],
  );

  const offerToShow = React.useMemo(() => {
    const bestOffer = offersByTripId?.[flight.fares[0]?.tripId];

    if (!bestOffer) return undefined;
    if (isSpendAndGetOffer(bestOffer.descriptions)) return undefined;
    if (bestOffer.amount.amount === credit?.amount.amount) return undefined;

    return bestOffer;
  }, [offersByTripId, credit]);

  const fareSlice = flights.fareSlices[getFareSliceId(selectedFare)];

  const isPackageExclusiveRatesExperiment =
    getExperimentVariant(expState.experiments, PACKAGES_TAG_EXCLUSIVE_RATES) ===
    AVAILABLE;
  const hasPackageExclusiveFare = flight.fares.some((fare: any) => {
    const flightFare = flights.fares[getFareId(fare)];
    return flightFare?.isPackageExclusiveRate === true;
  });
  const shouldShowPackageDeal =
    isPackageExclusiveRatesExperiment && hasPackageExclusiveFare;

  const currentPriceText: string = (() => {
    const fareSlice = flights.fareSlices[getFareSliceId(selectedFare)];
    const priceText =
      gridFares[FlightRatingsEnum[fareSlice.fareShelf.value]]?.price;
    if (!priceText?.startsWith("-")) {
      return `+${priceText}`;
    }
    return priceText;
  })();

  const { policies } = useContext(ClientContext);
  const showPolicyBanner =
    SHOW_POLICIES_IN_PACKAGES && useShowPolicyBanner(policies);

  const handleOnClick = () => {
    onClick?.(fareSlice.fareBrandName);
    if (!matchesLargeDesktop) {
      onFareClick?.(getFareId(selectedFare));
    }
  };

  const previousIsOutgoing = usePrevious(isOutgoing);

  const packageForFare = packagesByFareSlice[fareSlice.id];
  const packageNightCountBasedOnFare = dayjs(
    packageForFare?.lodgingStayDates?.until,
  ).diff(packageForFare?.lodgingStayDates?.from, "days");

  const fareNightCountDoesNotMatchAvailability =
    packageNightCountBasedOnAvailability !== null &&
    packageNightCountBasedOnFare !== packageNightCountBasedOnAvailability;
  const outboundNightCountDoesNotMatchAvailability =
    packageNightCountBasedOnOutboundFlight !== null &&
    packageNightCountBasedOnAvailability !== null &&
    packageNightCountBasedOnOutboundFlight !==
    packageNightCountBasedOnAvailability;
  const displayStayLengthBanner =
    fareNightCountDoesNotMatchAvailability ||
    // This is a small optimization to avoid unnecessary renders while switching pages
    (isOutgoing != previousIsOutgoing &&
      outboundNightCountDoesNotMatchAvailability);

  return (
    <Box
      className={clsx("flight-list-info-root", {
        recommended: isRecommendedFlight,
      })}
      onClick={
        matchesLargeDesktop || matchesMobile ? () => handleOnClick() : undefined
      }
    >
      {isRecommendedFlight && (
        <Box className="recommended-flight-banner">
          <Typography
            className="recommended-flight-banner-text"
            variant="body1"
          >
            {isCorpTenant(getConfigTenant())
              ? textConstants.CORPORATE_RECOMMENDED_FLIGHT_BANNER_TEXT
              : textConstants.RECOMMENDED_FLIGHT_BANNER_TEXT}
          </Typography>
        </Box>
      )}

      {matchesMobile ? (
        <Box className="flight-card-wrapper">
          <B2BFlightCardV2
            isMultiTicketType={slice.hasSelfTransferLayover}
            viNotificationBannerBox={() => <Box />}
            segments={slice.segments}
            duration={formatInterval(slice.totalDurationMinutes)}
            currentPriceText={currentPriceText}
            rewardText=""
            originCode={slice.origin}
            destinationCode={slice.destination}
            departureTime={slice.departure}
            arrivalTime={slice.arrival}
            airports={slice.segments
              .slice(0, slice.segments.length - 1)
              .map((s) => s.destination)}
            brandName={fareSlice?.fareBrandName ?? ""}
            primaryCarrier={airlineCode}
            isAgentPortal={isAgentPortal}
            airlineName={airlineName}
            tags={[]}
            bestOffer={offerToShow}
            showHeader={showPriceAndTags}
            earnString={
              showEarnEnhancement ? (
                <>
                  <Icon name={IconName.StarIcon} />
                  <SanitizedHtml
                    as={Typography}
                    className="earn-tag-text"
                    html={textConstants.getEarnTagText(
                      selectedRewardsAccount!.earn.flightsMultiplier,
                    )}
                  />
                  { }
                </>
              ) : undefined
            }
            earnTagClassName="b2b"
            viewPricingPrompt={isChatbotEnabled}
            isAirCXV3Experiment
          />
          {shouldShowPackageDeal && (
            <Box className="flight-package-savings-pill">
              <PackageSavingPill customText="Savings" useEnhancedUI tooltipContent="This is an exclusive package rate. The fare is lower than the cost you would pay for this flight if booked separately on Capital One Travel. Savings are applied to select fare types only and may not be available for all cabin classes." />
            </Box>
          )}
        </Box>
      ) : null}

      {matchesMediumDesktopOnly ? (
        <div>
          <ButtonWrap
            className="flight-row-wrapper"
            aria-label="More flight details"
            onClick={handleOnClick}
            aria-expanded={isExpanded}
          >
            <FlightShopRow
              isMultiTicketType={slice.hasSelfTransferLayover}
              segments={slice.segments}
              isAgentPortal={isAgentPortal}
              airlineCode={airlineCode}
              airlineName={airlineName}
              arrivalTime={slice.arrival}
              className={clsx("small-flight-shop-row", "b2b")}
              currentPriceText={showPriceAndTags ? currentPriceText : undefined}
              departureTime={slice.departure}
              destinationCode={slice.destination}
              duration={formatInterval(slice.totalDurationMinutes)}
              key={slice.id}
              layoverString={textConstants.getStopsString(slice.stops)}
              onClickTag={(label: string) => onAlgomerchClick(label)}
              originCode={slice.origin}
              tags={[]}
              tagsLimit={FARE_GRID_TAG_LIMIT}
              isChatbotEnabled={isChatbotEnabled}
              isAirCXV3Experiment
            />
            <TriangleIcon
              className={clsx("expand-flight-row-icon", {
                "is-expanded": isExpanded,
              })}
            />
          </ButtonWrap>
          {shouldShowPackageDeal && (
            <Box className="flight-package-savings-pill">
              <PackageSavingPill customText="Savings" useEnhancedUI tooltipContent="This is an exclusive package rate. The fare is lower than the cost you would pay for this flight if booked separately on Capital One Travel. Savings are applied to select fare types only and may not be available for all cabin classes." />
            </Box>
          )}
        </div>
      ) : null}

      {matchesLargeDesktop ? (
        <>
          <FlightGridRow
            viNotificationBannerBox={() => <Box />}
            key={slice.id}
            isMultiTicketType={slice.hasSelfTransferLayover}
            segments={slice.segments}
            isAgentPortal={isAgentPortal}
            tripType={
              isRoundTrip ? TripCategory.ROUND_TRIP : TripCategory.ONE_WAY
            }
            className={clsx("flight-grid-row", `row-${slice.id}`)}
            airlineCode={airlineCode}
            airlineName={airlineName}
            fareClassFilter={fareClassFilter}
            selectedMaxPrice={maxFlightPriceFilter}
            fares={gridFares}
            departureTime={slice.departure}
            arrivalTime={slice.arrival}
            originCode={slice.origin}
            destinationCode={slice.destination}
            duration={formatInterval(slice.totalDurationMinutes)}
            layoverString={textConstants.getStopsString(slice.stops)}
            onFareClick={onFareClick}
            algomerchModalOpen={false}
            onAlgomerchInfoClick={(label: string) => onAlgomerchClick(label)}
            tagsLimit={FARE_GRID_TAG_LIMIT}
            isExpanded={isExpanded}
            fareCardClassName="b2b"
            type="content"
            bestOffer={offerToShow}
            earnString={
              showEarnEnhancement ? (
                <>
                  <Icon name={IconName.StarIcon} />
                  <SanitizedHtml
                    as={Typography}
                    className="earn-tag-text"
                    html={textConstants.getEarnTagText(
                      selectedRewardsAccount!.earn.flightsMultiplier,
                    )}
                  />
                  { }
                </>
              ) : undefined
            }
            isChatbotEnabled={isChatbotEnabled}
            isAirCXV3Experiment
            showIncrementalPrices
            hideIsInPolicy={!showPolicyBanner}
          />
          {shouldShowPackageDeal && (
            <Box className="flight-package-savings-pill">
              <PackageSavingPill customText="Savings" useEnhancedUI tooltipContent="This is an exclusive package rate. The fare is lower than the cost you would pay for this flight if booked separately on Capital One Travel. Savings are applied to select fare types only and may not be available for all cabin classes." />
            </Box>
          )}
        </>
      ) : null}

      {displayStayLengthBanner && (
        <Box className="stay-length-banner">
          <Icon name={IconName.HotelFunnelIcon} />
          <Typography className="stay-length-text">
            {textConstants.STAY_LENGTH_TEXT(
              removeTimezone(slice.arrival),
              removeTimezone(slice.departure),
              packageNightCountBasedOnFare,
              packageNightCountBasedOnAvailability,
              packageNightCountBasedOnOutboundFlight,
              isOutgoing,
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export type IFlightListInfoWithSkeletonProps =
  | IFlightListInfoContentProps
  | IFlightListInfoSkeletonProps;

export const FlightListInfoWithSkeleton = (
  props: IFlightListInfoWithSkeletonProps,
) => {
  switch (props.type) {
    case "content":
      return <FlightListInfoContent {...props} />;
    case "skeleton":
      return (
        <Box className="flight-list-info-root">
          <FlightGridRow {...props} />
        </Box>
      );
    default:
      return <></>;
  }
};

const DESKTOP_OFFSET_SCROLL = 250;

const ctaTitles: FareDetailsCardCtaTitles = {
  primary: {
    getContent: (input: string, usePriceText?: boolean, isMobile?: boolean) =>
      usePriceText ? (
        <>
          {!isMobile ? "Continue for " : ""}
          <strong>{input} per traveler</strong>
        </>
      ) : (
        `Select ${input}`
      ),
    type: FareDetailsCardCtaType.Function,
  },
  secondary: undefined,
};

export interface IFlightListInfoProps {
  slice: Slice;
  index: number;
  showListView: boolean;
  expandedFlight: string;
  selectedFare: any;
  flights: any;
  flight: IFlightListData;
  fareClassFilter: FareclassOptionFilter;
  maxPriceFilter: number;
  isOutgoing: boolean;
  packageNightCountBasedOnAvailability: number | null;
  packageNightCountBasedOnOutboundFlight: number | null;
  packagesByOutboundFareSlice: { [key: string]: SafePackageDetails };
  packagesByReturnFareSlice: { [key: string]: SafePackageDetails };
  selectedFareId: string;
  departureDate: Date | null;
  returnDate: Date | null;
  hotelShopDateRange: DateRange | null;
  isTripDetailsLoading: boolean;
  expandedFareDetails: TripDetails | null;
  airports: {
    [key: string]: Airport;
  };
  isRecommendedFlight?: boolean;
  rewardsKey?: string;
  isAgentPortal?: boolean;
  selectedRewardsAccount?: RewardsAccount;
  canEarnRewards?: boolean;
  handleClickAlgomerchTag: (label: string) => void;
  setOpenModal: (value: React.SetStateAction<IOpenModal>) => void;
  handleSliceSelect: (sliceId: string, flight: IFlightListData) => void;
  setClickedFareId: (value: string) => void;
  setExpandedFlight: (value: string) => void;
  handleFareSelect: (
    flight: any,
    fareId: string,
    _idx: number,
    _limit?: FiatPrice | null,
  ) => void;
  isXmodRedesign?: boolean;
}

export const FlightListInfo = ({
  slice,
  index,
  showListView,
  expandedFlight,
  selectedFare,
  flights,
  flight,
  fareClassFilter,
  maxPriceFilter,
  isOutgoing,
  packageNightCountBasedOnAvailability,
  packageNightCountBasedOnOutboundFlight,
  packagesByOutboundFareSlice,
  packagesByReturnFareSlice,
  selectedFareId,
  departureDate,
  returnDate,
  hotelShopDateRange,
  isTripDetailsLoading,
  airports,
  isRecommendedFlight,
  rewardsKey,
  isAgentPortal,
  selectedRewardsAccount,
  canEarnRewards,
  expandedFareDetails,
  handleClickAlgomerchTag,
  setOpenModal,
  handleSliceSelect,
  setClickedFareId,
  setExpandedFlight,
  handleFareSelect,
  isXmodRedesign,
}: IFlightListInfoProps) => {
  const { matchesMobile, matchesDesktop, matchesLargeDesktop } =
    useDeviceTypes();

  const { isTravelApp } = useIsTravelApp();

  return (
    <Box
      id={slice.id}
      className={clsx(
        "flight-list-item",
        `flight-list-item-${index}`,
        "flight-row",
        {
          "row-view-desktop":
            matchesDesktop || (matchesLargeDesktop && showListView),
          expanded: slice.id === expandedFlight,
          recommended: isRecommendedFlight,
        },
        "b2b",
      )}
    >
      <FlightListInfoWithSkeleton
        type={FlightCardType.content}
        selectedFare={selectedFare}
        slice={slice}
        flights={flights}
        flight={flight}
        rewardsKey={rewardsKey || ""}
        fareClassFilter={fareClassFilter}
        onAlgomerchClick={(label: string) => {
          handleClickAlgomerchTag(label);
          setOpenModal(ModalTypes.AlgomerchModal);
        }}
        maxFlightPriceFilter={maxPriceFilter}
        isRoundTrip
        isOutgoing={isOutgoing}
        isAgentPortal={isAgentPortal}
        packageNightCountBasedOnAvailability={
          packageNightCountBasedOnAvailability
        }
        packageNightCountBasedOnOutboundFlight={
          packageNightCountBasedOnOutboundFlight
        }
        packagesByFareSlice={
          isOutgoing ? packagesByOutboundFareSlice : packagesByReturnFareSlice
        }
        isRecommendedFlight={isRecommendedFlight}
        selectedRewardsAccount={selectedRewardsAccount}
        canEarnRewards={canEarnRewards}
        onClick={() => {
          handleSliceSelect(slice.id, flight);
          setTimeout(() => {
            const OFFSET = DESKTOP_OFFSET_SCROLL;
            const cardTop =
              document?.getElementById(slice.id)?.getBoundingClientRect().top ||
              0;
            window.scrollBy({
              top: (cardTop as number) - OFFSET,
              behavior: "smooth",
            });
          }, 100);
        }}
        onFareClick={(fareId: string) => {
          setClickedFareId(fareId);
        }}
        departureDate={departureDate}
        returnDate={returnDate}
        hotelShopDateRange={hotelShopDateRange}
      />
      {matchesMobile && slice.id === expandedFlight && (
        <MobilePopoverCard
          headerElement={
            <Header
              className="mobile-flight-details-header-container"
              left={
                isTravelApp ? undefined : (
                  <BackButton
                    className="back-button-icon"
                    onClick={() => {
                      setExpandedFlight("");
                    }}
                  />
                )
              }
              center={
                <Typography
                  variant="body1"
                  className="mobile-flight-details-header"
                >
                  Choose Fare
                </Typography>
              }
              isMobile
              fullWidth
            />
          }
          open
          fullScreen
          className="mobile-flight-details-container"
          contentClassName="mobile-flight-details-container-content"
          centered
        >
          {(!expandedFareDetails || isTripDetailsLoading) && (
            <LoadingIndicator
              className="packages-flight-shop-details-loading-indicator"
              indicatorSize="small"
              indicator={B2BSpinner}
              message={textConstants.LOADING_FLIGHT_DETAILS_STRING}
            />
          )}
          {expandedFareDetails && !isTripDetailsLoading && (
            <FlightDetails
              isOutgoing={isOutgoing}
              selectedFareId={selectedFareId}
              onFareClick={(fareId, limit) => {
                handleFareSelect(flight, fareId, index, limit);
              }}
              tripDetails={expandedFareDetails}
              rewardsKey={undefined}
              departureDate={undefined}
              returnDate={undefined}
              airports={{}}
              isSpiritOrFrontierAirlinesSelected={false}
              onAlgomerchClick={(label: string) => {
                handleClickAlgomerchTag(label);
                setOpenModal(ModalTypes.AlgomerchModal);
              }}
              ctaTitles={ctaTitles}
              showFareDetailsTitle
              isMobile={matchesMobile}
              isXmodRedesign={isXmodRedesign}
            />
          )}
        </MobilePopoverCard>
      )}
      {slice.id === expandedFlight &&
        expandedFareDetails &&
        !isTripDetailsLoading && (
          <FlightDetails
            isOutgoing={isOutgoing}
            selectedFareId={selectedFareId}
            onFareClick={(fareId, limit) => {
              handleFareSelect(flight, fareId, index, limit);
            }}
            tripDetails={expandedFareDetails}
            isSpiritOrFrontierAirlinesSelected={false}
            onAlgomerchClick={(label: string) => {
              handleClickAlgomerchTag(label);
              setOpenModal(ModalTypes.AlgomerchModal);
            }}
            ctaTitles={ctaTitles}
            showFareDetailsTitle
            isMobile={matchesMobile}
            isXmodRedesign={isXmodRedesign}
            {...{ rewardsKey, departureDate, returnDate, airports }}
          />
        )}

      {slice.id === expandedFlight &&
        (!expandedFareDetails || isTripDetailsLoading) && (
          <LoadingIndicator
            className="packages-flight-shop-details-loading-indicator"
            indicatorSize="small"
            indicator={B2BSpinner}
            message={textConstants.LOADING_FLIGHT_DETAILS_STRING}
          />
        )}
    </Box>
  );
};
